'use client';

import { useSearchParams } from 'next/navigation';
import { type FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { unlockAccount } from '@/api/auth';
import { Modals } from '@/helpers/modals';
import { showTooltip } from '@/helpers/tooltip';
import { useLogin } from '@/hooks/login';
import { useNavigation } from '@/hooks/navigation';
import { useSwrMutation } from '@/hooks/swr';
import { useGetCommonStore, useModalsStore } from '@/store';
import { TooltipTypes } from '@/store/tooltipStore';

import { Spinner } from '@/styles/common';

export const UnlockAccount: FC = () => {
  const { t } = useTranslation('common');
  const { blackColor, mainColor } = useTheme();

  const searchParams = useSearchParams();
  const { navigate } = useNavigation();
  const open = useModalsStore.use.open();
  const useCommonStore = useGetCommonStore();

  const { isAuthenticated, pendingUser, user } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      pendingUser: state.pendingUser,
      user: state.user,
    }))
  );

  const { resetRequestCount: resetRequestCountLogin } = useLogin();

  const {
    trigger: requestUnlockAccount,
    data: unlockAccountData,
    error: unlockAccountError,
    isMutating,
  } = useSwrMutation({
    key: 'unlockAccount',
    fetcher: unlockAccount,
  });

  const handleModalClose = useCallback(() => navigate('/', true), [navigate]);

  const unlockToken =
    searchParams.get('unlock_token') || searchParams.get('unlockToken');

  useEffect(() => {
    if (unlockToken && !pendingUser) {
      requestUnlockAccount(unlockToken);

      resetRequestCountLogin();
    }
  }, [unlockToken, requestUnlockAccount, pendingUser, resetRequestCountLogin]);

  useEffect(() => {
    if (pendingUser) {
      return;
    }

    useCommonStore.setState({ afterLoginRedirectUrl: '/' });

    if (unlockToken) {
      if (isAuthenticated) {
        navigate('/', true);
      }
    } else {
      open(Modals.Login, { customOnClose: handleModalClose });
    }
  }, [
    isAuthenticated,
    pendingUser,
    navigate,
    requestUnlockAccount,
    searchParams,
    user?.id,
    open,
    unlockToken,
  ]);

  useEffect(() => {
    if (unlockAccountData || unlockAccountError) {
      if (unlockAccountError) {
        showTooltip({
          type: TooltipTypes.ERROR,
          body: `${t('errorUnknown')}`,
        });
      } else {
        showTooltip({
          type: TooltipTypes.SUCCESS,
          body: `${t('auth.login.infoAccountUnlocked')}`,
        });
        navigate('/', true);
      }

      open(Modals.Login, { customOnClose: handleModalClose });
    }
  }, [
    unlockAccountData,
    unlockAccountError,
    navigate,
    resetRequestCountLogin,
    t,
    open,
  ]);

  return isMutating ? (
    <Spinner
      background={blackColor}
      color={mainColor}
      margin="16px auto"
      size={60}
    />
  ) : null;
};
